import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { Badge } from '../atoms';
import {
  StatCard, StatCardRow,
  DataTable,
  ActivityPanel,
  ActivityEvent,
} from '../organisms';

/**
 * Dashboard — CoreOS operational landing page.
 * Shows work orders in progress, accounts count, recent activity.
 */

interface DashboardResponse {
  stats: {
    accounts: number;
    contacts: number;
    workOrders: number;
    workOrdersInProgress: number;
    assets: number;
    serviceResources: number;
  };
  recentWorkOrders: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    account_name: string | null;
    created_at: string;
  }>;
  recentActivity: Array<{
    id: string;
    entity_type: string;
    action: string;
    user_email: string | null;
    created_at: string;
  }>;
}

const statusVariant: Record<string, 'success' | 'warning' | 'info' | 'danger' | 'neutral'> = {
  completed: 'success',
  in_progress: 'info',
  scheduled: 'warning',
  new: 'neutral',
  cancelled: 'danger',
  on_hold: 'warning',
};

const workOrderColumns: ColumnDef<DashboardResponse['recentWorkOrders'][number]>[] = [
  { accessorKey: 'title', header: 'Title' },
  { accessorKey: 'account_name', header: 'Account' },
  {
    accessorKey: 'priority',
    header: 'Priority',
    cell: ({ getValue }) => {
      const v = String(getValue() || 'normal');
      return <Badge variant={v === 'high' || v === 'urgent' ? 'danger' : v === 'low' ? 'neutral' : 'info'}>{v}</Badge>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const s = String(getValue() || 'new');
      return <Badge variant={statusVariant[s] ?? 'neutral'} dot>{s.replace(/_/g, ' ')}</Badge>;
    },
  },
  { accessorKey: 'created_at', header: 'Created', cell: ({ getValue }) => {
    const v = getValue() as string;
    return v ? new Date(v).toLocaleDateString() : '—';
  }},
];

export function Dashboard() {
  const query = useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => api.get<DashboardResponse>('/api/dashboard', signal),
  });

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading dashboard...</p>;
  }
  if (query.error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <strong>Failed to load dashboard:</strong> {(query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;

  const data = query.data;

  const activityEvents: ActivityEvent[] = (data.recentActivity || []).slice(0, 8).map(ev => ({
    id: ev.id,
    kind: ev.action === 'delete' ? 'danger' : ev.action === 'create' ? 'success' : 'info',
    title: <>{ev.entity_type.replace(/_/g, ' ')} &middot; <strong>{ev.action}</strong></>,
    description: ev.user_email || 'system',
    timestamp: new Date(ev.created_at).toLocaleString(),
  }));

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-6)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Dashboard</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          CoreOS operational overview — {data.stats.accounts} accounts, {data.stats.contacts} contacts
        </p>
      </header>

      <div style={{ marginBottom: 'var(--sp-7)' }}>
        <StatCardRow>
          <StatCard
            label="Accounts"
            value={String(data.stats.accounts)}
            caption="total registered accounts"
            accent="hunter"
          />
          <StatCard
            label="Contacts"
            value={String(data.stats.contacts)}
            caption="across all accounts"
            accent="info"
          />
          <StatCard
            label="Work orders"
            value={String(data.stats.workOrdersInProgress)}
            caption={`of ${data.stats.workOrders} total in progress`}
            accent="amber"
          />
          <StatCard
            label="Assets"
            value={String(data.stats.assets)}
            caption={`${data.stats.serviceResources} service resources`}
            accent="green"
          />
        </StatCardRow>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 360px',
        gap: 'var(--sp-5)',
        alignItems: 'start',
      }}>
        <section>
          <header style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 'var(--sp-3)',
          }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--fs-xl)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--color-ink)',
            }}>Recent work orders</h2>
            <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              {data.recentWorkOrders.length} orders
            </span>
          </header>
          {data.recentWorkOrders.length === 0 ? (
            <div style={{
              padding: 'var(--sp-6)',
              background: 'var(--color-card)',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--r-lg)',
              color: 'var(--color-muted)',
              textAlign: 'center',
              fontSize: 'var(--fs-md)',
            }}>No work orders yet.</div>
          ) : (
            <DataTable data={data.recentWorkOrders} columns={workOrderColumns} />
          )}
        </section>

        <ActivityPanel
          title="Recent activity"
          events={activityEvents}
          emptyMessage="No recent activity logged."
        />
      </div>
    </>
  );
}
